import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface AnnouncementContextType {
  showFeatureAnnouncement: boolean;
  showFeatureBanner: boolean;
  dismissFeatureAnnouncement: () => void;
  dismissFeatureBanner: () => void;
  exploreStockFeature: () => void;
  resetAnnouncements: () => void;
}

const AnnouncementContext = createContext<AnnouncementContextType | undefined>(
  undefined
);

export const useAnnouncement = () => {
  const context = useContext(AnnouncementContext);
  if (!context) {
    throw new Error("useAnnouncement must be used within AnnouncementProvider");
  }
  return context;
};

interface AnnouncementProviderProps {
  children: React.ReactNode;
}

export const AnnouncementProvider: React.FC<AnnouncementProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [showFeatureAnnouncement, setShowFeatureAnnouncement] = useState(false);
  const [showFeatureBanner, setShowFeatureBanner] = useState(false);

  // Check if user has seen the announcement
  useEffect(() => {
    const hasSeenAnnouncement = localStorage.getItem("hasSeenStockPOSAnnouncement");
    const hasSeenBanner = localStorage.getItem("hasSeenStockPOSBanner");
    
    if (!hasSeenAnnouncement) {
      // Show announcement after a short delay for better UX
      const timer = setTimeout(() => {
        setShowFeatureAnnouncement(true);
      }, 2000);
      
      return () => clearTimeout(timer);
    } else if (!hasSeenBanner) {
      // If they've seen the main announcement but not the banner, show banner
      setShowFeatureBanner(true);
    }
  }, []);

  const dismissFeatureAnnouncement = () => {
    setShowFeatureAnnouncement(false);
    localStorage.setItem("hasSeenStockPOSAnnouncement", "true");
    
    // Show banner for ongoing awareness
    setTimeout(() => {
      setShowFeatureBanner(true);
    }, 1000);
  };

  const dismissFeatureBanner = () => {
    setShowFeatureBanner(false);
    localStorage.setItem("hasSeenStockPOSBanner", "true");
  };

  const exploreStockFeature = () => {
    setShowFeatureAnnouncement(false);
    setShowFeatureBanner(false);
    localStorage.setItem("hasSeenStockPOSAnnouncement", "true");
    localStorage.setItem("hasSeenStockPOSBanner", "true");
    
    // Navigate to stock management
    navigate("/stock");
  };

  const resetAnnouncements = () => {
    localStorage.removeItem("hasSeenStockPOSAnnouncement");
    localStorage.removeItem("hasSeenStockPOSBanner");
    setShowFeatureAnnouncement(true);
    setShowFeatureBanner(false);
  };

  return (
    <AnnouncementContext.Provider
      value={{
        showFeatureAnnouncement,
        showFeatureBanner,
        dismissFeatureAnnouncement,
        dismissFeatureBanner,
        exploreStockFeature,
        resetAnnouncements,
      }}
    >
      {children}
    </AnnouncementContext.Provider>
  );
};
